import React, { useState } from "react";
import { Button } from "../design/button";
import { Divider } from "../design/divider";
import { ErrorMessage, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Field } from "@headlessui/react";

import logo from "../assets/logo.png";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { Link } from "../design/link";
import { Text } from "../design/text";

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  businessName?: string;
  businessWebsite?: string;
}

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Errors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Failed to create account", error);
    }
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First Name is required";
    if (!lastName) newErrors.lastName = "Last Name is required";
    if (!email) newErrors.email = "Email is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="flex items-center justify-center min-h-screen">
      <form
        method="post"
        className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900"
        onSubmit={handleSubmit}
      >
        <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
        <Fieldset>
          <Heading className="text-2xl font-semibold text-center">
            Register
          </Heading>
          <Divider className="my-6" />
          <FieldGroup>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={firstName}
                  placeholder="First Name"
                  onChange={(e) => setFirstName(e.target.value)}
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={lastName}
                  placeholder="Last Name"
                  onChange={(e) => setLastName(e.target.value)}
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                aria-label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </Field>
          </FieldGroup>
          <Divider className="my-6" />
          <Button type="submit" className="w-full">
            Register
          </Button>
          <div className="my-6 text-center">
            <Text>
              Already have an account?{" "}
              <Link href="/login" className="text-blue-600 hover:underline">
                Login here
              </Link>
            </Text>
          </div>
        </Fieldset>
      </form>
    </div>
  );
};

export default RegisterPage;
